import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import styles from './ErrorBoundary.module.css';

interface Props {
  children: ReactNode;
  /** Where "Back to start" goes; scoped boundaries can offer a local reset instead. */
  onReset?(): void;
  /** Compact inline variant for a single panel. */
  inline?: boolean;
  label?: string;
}

interface State {
  error: Error | null;
}

/** Catches render errors so one broken panel never blanks the whole app. */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('UI error', error, info.componentStack);
  }

  private reset = () => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className={this.props.inline ? styles.inline : styles.page} role="alert">
        <span className={styles.icon}>
          <Icon name="alert" size={32} />
        </span>
        <h1 className={styles.title}>{this.props.label ?? 'Something went wrong'}</h1>
        <p className={styles.text}>
          This part of the app hit a problem. Your match is safe on the server — reloading usually fixes it.
        </p>
        <div className={styles.actions}>
          <Button variant="primary" size="l" icon="refresh" onClick={() => location.reload()}>
            Reload
          </Button>
          <Button variant="secondary" size="l" onClick={this.reset}>
            Try again
          </Button>
        </div>
      </div>
    );
  }
}
